import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const RegistrationForm = ({ onSubmit, isLoading = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    industry: '',
    companySize: '',
    password: '',
    confirmPassword: '',
    accountType: 'individual',
    agreeToTerms: false,
    subscribeNewsletter: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const industryOptions = [
    { value: '', label: 'Select Industry' },
    { value: 'government', label: 'Government Agency' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & Commerce' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'telecommunications', label: 'Telecommunications' },
    { value: 'transportation', label: 'Transportation & Logistics' },
    { value: 'energy', label: 'Energy & Utilities' },
    { value: 'construction', label: 'Construction & Real Estate' },
    { value: 'ngo', label: 'NGO & Non-Profit' },
    { value: 'other', label: 'Other' }
  ];

  const companySizeOptions = [
    { value: '', label: 'Select Company Size' },
    { value: 'startup', label: 'Startup (1-10 employees)' },
    { value: 'small', label: 'Small Business (11-50 employees)' },
    { value: 'medium', label: 'Medium Enterprise (51-200 employees)' },
    { value: 'large', label: 'Large Enterprise (201-1000 employees)' },
    { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateUgandanPhone = (phone) => {
    const phoneRegex = /^(\+256|0)(7[0-9]{8}|3[0-9]{8})$/;
    return phoneRegex?.test(phone?.replace(/\s/g, ''));
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 1;
    if (/[a-z]/?.test(password)) strength += 1;
    if (/[A-Z]/?.test(password)) strength += 1;
    if (/[0-9]/?.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/?.test(password)) strength += 1;
    return strength;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Real-time password strength calculation
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Personal details validation
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validateUgandanPhone(formData?.phone)) {
      newErrors.phone = 'Please enter a valid Ugandan phone number (e.g., +256 700 123 456)';
    }

    // Company details validation (for enterprise accounts)
    if (formData?.accountType === 'enterprise') {
      if (!formData?.companyName?.trim()) {
        newErrors.companyName = 'Company name is required for enterprise accounts';
      }
      if (!formData?.industry) {
        newErrors.industry = 'Industry selection is required';
      }
      if (!formData?.companySize) {
        newErrors.companySize = 'Company size is required';
      }
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (passwordStrength < 3) {
      newErrors.password = 'Password is too weak. Include uppercase, lowercase, numbers, and symbols';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms agreement validation
    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'bg-error';
      case 2:
        return 'bg-warning';
      case 3:
        return 'bg-accent';
      case 4:
      case 5:
        return 'bg-success';
      default:
        return 'bg-muted';
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'Very Weak';
      case 2:
        return 'Weak';
      case 3:
        return 'Fair';
      case 4:
        return 'Good';
      case 5:
        return 'Strong';
      default:
        return '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Account Type Selection */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="Users" size={20} className="mr-2" />
          Account Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
            formData?.accountType === 'individual' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}>
            <input
              type="radio"
              name="accountType"
              value="individual"
              checked={formData?.accountType === 'individual'}
              onChange={(e) => handleInputChange('accountType', e?.target?.value)}
              className="sr-only"
            />
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <Icon name="User" size={20} className="mr-2 text-primary" />
                <span className="font-medium text-foreground">Individual Customer</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Personal account for individual purchases and basic RFQ services
              </p>
            </div>
            {formData?.accountType === 'individual' && (
              <Icon name="CheckCircle" size={20} className="text-primary ml-2" />
            )}
          </label>

          <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
            formData?.accountType === 'enterprise' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}>
            <input
              type="radio"
              name="accountType"
              value="enterprise"
              checked={formData?.accountType === 'enterprise'}
              onChange={(e) => handleInputChange('accountType', e?.target?.value)}
              className="sr-only"
            />
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <Icon name="Building" size={20} className="mr-2 text-primary" />
                <span className="font-medium text-foreground">Enterprise Client</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Business account with advanced features and priority support
              </p>
            </div>
            {formData?.accountType === 'enterprise' && (
              <Icon name="CheckCircle" size={20} className="text-primary ml-2" />
            )}
          </label>
        </div>
      </div>
      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="UserCheck" size={20} className="mr-2" />
          Personal Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your email address"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          description="We'll use this for account verification and important updates"
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          placeholder="+256 700 123 456"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          description="Ugandan mobile number for SMS notifications and support"
          required
        />
      </div>
      {/* Company Information (Enterprise Only) */}
      {formData?.accountType === 'enterprise' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="Building2" size={20} className="mr-2" />
            Company Information
          </h3>
          
          <Input
            label="Company Name"
            type="text"
            placeholder="Enter your company name"
            value={formData?.companyName}
            onChange={(e) => handleInputChange('companyName', e?.target?.value)}
            error={errors?.companyName}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Industry"
              options={industryOptions}
              value={formData?.industry}
              onChange={(value) => handleInputChange('industry', value)}
              error={errors?.industry}
              placeholder="Select your industry"
              required
            />
            <Select
              label="Company Size"
              options={companySizeOptions}
              value={formData?.companySize}
              onChange={(value) => handleInputChange('companySize', value)}
              error={errors?.companySize}
              placeholder="Select company size"
              required
            />
          </div>
        </div>
      )}
      {/* Account Security */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="Shield" size={20} className="mr-2" />
          Account Security
        </h3>
        
        <div className="space-y-2">
          <Input
            label="Password"
            type="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          
          {formData?.password && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Password Strength:</span>
                <span className={`font-medium ${
                  passwordStrength >= 4 ? 'text-success' : 
                  passwordStrength >= 3 ? 'text-accent' : 
                  passwordStrength >= 2 ? 'text-warning' : 'text-error'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
      </div>
      {/* Terms and Preferences */}
      <div className="space-y-4">
        <div className="space-y-3">
          <Checkbox
            label="I agree to the Terms of Service and Privacy Policy"
            description="By creating an account, you agree to our terms and conditions"
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />
          
          <Checkbox
            label="Subscribe to newsletter and product updates"
            description="Receive updates about new ICT solutions and special offers"
            checked={formData?.subscribeNewsletter}
            onChange={(e) => handleInputChange('subscribeNewsletter', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
        disabled={!formData?.agreeToTerms}
      >
        Create Account
      </Button>
    </form>
  );
};

export default RegistrationForm;